<template>
  <div class="space-y-2">
    <template v-for="item in sortedMenuItems" :key="item.key">
      <RouterLink
        v-if="item.route"
        :to="{ name: item.route }"
        :class="itemClass(item.activeViews)"
        @click="handleClose"
      >
        <component :is="item.icon" class="w-5 h-5" />
        <span>{{ translate(`nav.${item.key}`) }}</span>
      </RouterLink>
      <button v-else type="button" :class="itemClass()" @click="handleAction(item.action)">
        <component :is="item.icon" class="w-5 h-5" />
        <span>{{ translate(`nav.${item.key}`) }}</span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { ViewType } from "@core/composables/state/useNavigation";
import IconBankNote from "@core/icons/IconBankNote.vue";
import IconBook from "@core/icons/IconBook.vue";
import IconBriefcase from "@core/icons/IconBriefcase.vue";
import IconCamera from "@core/icons/IconCamera.vue";
import IconCar from "@core/icons/IconCar.vue";
import IconChartBar from "@core/icons/IconChartBar.vue";
import IconCheckCircle from "@core/icons/IconCheckCircle.vue";
import IconDatabase from "@core/icons/IconDatabase.vue";
import IconDownload from "@core/icons/IconDownload.vue";
import IconServer from "@core/icons/IconServer.vue";
import IconShield from "@core/icons/IconShield.vue";
import IconSignal from "@core/icons/IconSignal.vue";
import IconUsers from "@core/icons/IconUsers.vue";
import IconWindow from "@core/icons/IconWindow.vue";
import { RouterLink } from "vue-router";

import { useTranslate } from "@common/composables/useTranslate";

import { sortAdminMenuItemsByLabel, type AdminMenuAction, type AdminMenuItem } from "./adminNavigationMenu";

type AdminPermissions = {
  statistics: boolean;
  telemetry: boolean;
  applications: boolean;
  users: boolean;
  organizations: boolean;
  audits: boolean;
  remits: boolean;
  vehicleRegistry: boolean;
  manualApproval: boolean;
  trafficControl: boolean;
  system: boolean;
  camerasOverview: boolean;
};

const props = defineProps<{
  permissions: AdminPermissions;
  canAccessYouParkCustomer: boolean;
  activeView: ViewType;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "navigate-customers"): void;
  (e: "navigate-customers-remit"): void;
}>();

const translate = useTranslate();

const menuItems = computed<AdminMenuItem[]>(() => [
  {
    key: "statistics",
    icon: IconChartBar,
    visible: props.permissions.statistics,
    route: "statistics",
    activeViews: ["statistics"]
  },
  {
    key: "telemetry",
    icon: IconSignal,
    visible: props.permissions.telemetry,
    route: "telemetry",
    activeViews: ["telemetry"]
  },
  {
    key: "applications",
    icon: IconWindow,
    visible: props.permissions.applications,
    route: "applications",
    activeViews: ["applications"]
  },
  {
    key: "users",
    icon: IconUsers,
    visible: props.permissions.users,
    route: "users",
    activeViews: ["users", "user-edit", "user-create"]
  },
  {
    key: "organizations",
    icon: IconBook,
    visible: props.permissions.organizations,
    route: "organizations",
    activeViews: [
      "organizations",
      "organizations-create",
      "organizations-details",
      "organizations-details-applications",
      "organizations-details-users",
      "organizations-details-subs"
    ]
  },
  { key: "audits", icon: IconDatabase, visible: props.permissions.audits, route: "audits", activeViews: ["audits"] },
  { key: "exports", icon: IconDownload, visible: props.permissions.audits, route: "exports", activeViews: ["exports"] },
  { key: "remit", icon: IconBankNote, visible: props.permissions.remits, action: "navigate-customers-remit" },
  {
    key: "vehicleRegistry",
    icon: IconCar,
    visible: props.permissions.vehicleRegistry,
    route: "vehicle-registry",
    activeViews: ["vehicle-registry"]
  },
  {
    key: "manualApproval",
    icon: IconCheckCircle,
    visible: props.permissions.manualApproval,
    route: "manual-approval",
    activeViews: ["manual-approval"]
  },
  {
    key: "camerasOverview",
    icon: IconCamera,
    visible: props.permissions.camerasOverview,
    route: "cameras-overview",
    activeViews: ["cameras-overview"]
  },
  {
    key: "trafficControl",
    icon: IconShield,
    visible: props.permissions.trafficControl,
    route: "traffic-control",
    activeViews: ["traffic-control"]
  },
  { key: "system", icon: IconServer, visible: props.permissions.system, route: "system", activeViews: ["system"] },
  {
    key: "youparkCustomers",
    icon: IconBriefcase,
    visible: props.canAccessYouParkCustomer,
    action: "navigate-customers"
  }
]);

const sortedMenuItems = computed(() =>
  sortAdminMenuItemsByLabel(
    menuItems.value.filter(item => item.visible),
    translate
  )
);

const handleClose = () => {
  emit("close");
};

const handleAction = (action?: AdminMenuAction) => {
  handleClose();
  if (action === "navigate-customers") {
    emit("navigate-customers");
  } else if (action === "navigate-customers-remit") {
    emit("navigate-customers-remit");
  }
};

const baseClass = "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left";
const inactiveClass =
  "text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white";
const activeClass = "bg-green-500 text-white font-medium shadow-sm";

const itemClass = (view?: ViewType | ViewType[]) => {
  const views = Array.isArray(view) ? view : view ? [view] : [];
  const isActive = views.length > 0 && views.includes(props.activeView);
  return isActive ? `${baseClass} ${activeClass}` : `${baseClass} ${inactiveClass}`;
};
</script>
